import { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { DateTime } from "luxon"
import { PlayCircle } from "lucide-react"
import { openInNewTab } from "../../../utils/downloadFile"
import Table from "../../table"
import EngagementsCell from "./cells/engagements"
import MenuCell from "./cells/menu"
import OwnerCell from "./cells/owner"
import ViewDashboardCell from "./cells/view-dashboard"
import { LinkBtn } from "../../buttons"
import ManageEngagementPanel from "../manage"
import HowItWorksModal from "../../how-it-works-modal"
import {
  useFetchSubscriptionDetailsQuery,
  useFetchSubscriptionLogsQuery
} from "../../../../redux/queries"
import { useAppDispatch, useAppSelector } from "../../../hooks"
import { selectUserDetails } from "../../../../redux/slices/auth"
import {
  selectTabRange,
  updateRange
} from "../../../../redux/slices/engagements"
import {
  checkIsAdmin,
  checkIsSuperAdmin,
  copyToClipboard
} from "../../../utils"
import {
  SubscriptionLog,
  BBColumnDef,
  EngagementsTab,
  DateRange
} from "../../../../types"
import DaysFilter from "../../days-filter"
import { FilterFnOption } from "@tanstack/react-table"

const EngagementsTable = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const userDetails = useAppSelector(selectUserDetails)

  const isAdmin = useMemo(
    () => checkIsAdmin(userDetails?.roles ?? []),
    [userDetails?.roles]
  )
  const isSuperAdmin = useMemo(
    () => checkIsSuperAdmin(userDetails?.roles ?? []),
    [userDetails?.roles]
  )

  const loid = userDetails?.id
  const orgId = userDetails?.org?.id
  const [selectedSubscriptionId, setSelectedSubscriptionId] =
    useState<string>("")
  const [showHowItWorksModal, setShowHowItWorksModal] = useState(false)

  const {
    data: selectedSubscription,
    isFetching: isFetchingSelectedSubscription
  } = useFetchSubscriptionDetailsQuery(
    { id: selectedSubscriptionId },
    { skip: !selectedSubscriptionId }
  )

  // Keep panel closed during refetch to prevent it from reopening
  // Only close if we're refetching AND we already have data (not initial load)
  useEffect(() => {
    if (
      isFetchingSelectedSubscription &&
      selectedSubscriptionId &&
      selectedSubscription
    ) {
      // During refetch after update, ensure panel stays closed
      setSelectedSubscriptionId("")
    }
  }, [
    isFetchingSelectedSubscription,
    selectedSubscriptionId,
    selectedSubscription
  ])

  const openLink = useCallback((link: string) => {
    if (link) {
      openInNewTab(link)
    }
  }, [])

  const openLearnVideo = useCallback(() => setShowHowItWorksModal(true), [])

  const navigateToDetails = useCallback(
    (id: string) => navigate(`/engagements/${id}`),
    [navigate]
  )

  const resetSelectedSubscriptionId = useCallback(
    () => setSelectedSubscriptionId(""),
    []
  )

  const columns: BBColumnDef<SubscriptionLog>[] = useMemo(
    () => [
      {
        id: "engagements",
        header: "Subscriptions",
        accessorFn: row => `${row.first_name} ${row.last_name} ${row.email}`,
        footer: props => props.column.id,
        cell: props => (
          <EngagementsCell
            info={props.row.original}
            onClick={navigateToDetails}
          />
        ),
        colSpan: 2,
        filterFn:
          "rowStatusFilter" as unknown as FilterFnOption<SubscriptionLog>
      },
      {
        id: "owner",
        header: "Owner",
        accessorFn: row => row.owner_email,
        footer: props => props.column.id,
        cell: props => (
          <OwnerCell info={props.row.original} onClick={navigateToDetails} />
        ),
        meta: { headerClassName: "hidden md:table-cell" }
      },
      {
        id: "view-dashboard",
        header: "",
        accessorFn: row => row.id,
        footer: props => props.column.id,
        cell: props => (
          <ViewDashboardCell info={props.row.original} openLink={openLink} />
        ),
        meta: { headerClassName: "hidden md:table-cell" },
        enableSorting: false
      },
      {
        id: "created",
        header: "",
        accessorFn: row => row.created,
        footer: props => props.column.id,
        cell: props => "",
        colSpan: 0,
        meta: { headerClassName: "hidden" }
      },
      {
        id: "edit",
        header: "",
        footer: props => props.column.id,
        enableSorting: false,
        cell: props => (
          <MenuCell
            info={props.row.original}
            onCopyDashboardLink={copyToClipboard}
            onEdit={setSelectedSubscriptionId}
            onViewLeadDashboard={openLink}
          />
        )
      }
    ],
    [navigateToDetails, openLink]
  )

  const tabKey = EngagementsTab.engagements
  const range = useAppSelector(state => selectTabRange(state, tabKey))
  const handleUpdateRange = useCallback(
    (newValue: DateRange) => {
      dispatch(updateRange({ tabKey, newValue }))
    },
    [dispatch, tabKey]
  )

  const minCreated = useMemo(
    () => DateTime.now().minus({ days: range }),
    [range]
  )

  // subscription logs
  const { data: subscriptionLogs, isFetching } = useFetchSubscriptionLogsQuery({
    min_created: Math.floor(minCreated.toSeconds()),
    limit: 99999,
    offset: 0,
    ...(isSuperAdmin ? {} : isAdmin ? { org_id: orgId } : { loid })
  })

  return (
    <div>
      <Table<SubscriptionLog>
        className="engagements-table"
        isLoading={isFetching}
        data={subscriptionLogs || []}
        columns={columns}
        viewType="table"
        dropdownFilterColumnId="engagements"
        searchPlaceholder="Search subscriptions…"
        extraHeaderElement={
          <DaysFilter range={range} handleUpdateRange={handleUpdateRange} />
        }
        extraHeaderElementClassName="ml-auto"
        shrinkFilterSort
        defaultSortBy="created"
        defaultSortDir="desc"
        extraElementBeforeTable={
          <div className="px-2">
            <LinkBtn className="-my-1" onClick={openLearnVideo}>
              <PlayCircle size={20} />
              <span className="ml-2">Learn how it works</span>
            </LinkBtn>
          </div>
        }
        hasColumnFilter={false}
        hasActiveDeletedFilter
      />

      <ManageEngagementPanel
        data={selectedSubscription}
        open={!!(selectedSubscriptionId && selectedSubscription)}
        onClose={resetSelectedSubscriptionId}
      />

      <HowItWorksModal
        videoType="engagements"
        show={showHowItWorksModal}
        toggleModal={setShowHowItWorksModal}
      />
    </div>
  )
}

export default EngagementsTable
